import { useEffect, useState, useMemo, Fragment } from "react";
import {
  Container, Typography, Box, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, IconButton, CircularProgress,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { AddRounded, DeleteOutline, GroupsRounded, EditOutlined } from "@mui/icons-material";
import PageHeader from "../../components/PageHeader";
import ApiCall from "../../utils/apicall";
import { showSuccessToast, showErrorToast } from "../../utils/Toast";

const ratingBg = (v) => { if (v === "" || v == null) return "transparent"; const n = Number(v); if (n >= 7) return "#E6F4EA"; if (n >= 4) return "#FFF4E5"; return "#FDECEA"; };
const ratingFg = (v) => { const n = Number(v); if (n >= 7) return "#1E7E34"; if (n >= 4) return "#B26A00"; return "#C62828"; };
const head = { position: "sticky", top: 0, background: "#1D2330", color: "#fff", padding: "7px 6px", fontWeight: 600, fontSize: "0.7rem", borderRight: "1px solid #2A3340", textAlign: "center", zIndex: 2 };
const stickyHead = { ...head, left: 0, zIndex: 4, textAlign: "left", minWidth: 170 };
const grpHead = { background: "#2A3340", color: "#fff", padding: "5px 8px", fontSize: "0.68rem", fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", borderRight: "2px solid #1D2330", cursor: "pointer", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 2 };
const cell = { padding: "2px 6px", borderRight: "1px solid #EEF0F3", borderBottom: "1px solid #EEF0F3", textAlign: "center" };
const stickyCell = { ...cell, position: "sticky", left: 0, background: "#fff", textAlign: "left", minWidth: 170, zIndex: 1 };

function groupOf(c) {
  const n = (c.name || "").toLowerCase();
  if (c.col_type === "rating") return "Core competencies";
  if (n.startsWith("element") || n.includes("certification training")) return "AISC element training";
  if (n.includes("cert") || n.includes("training") || n.includes("other skills")) return "Certifications & training";
  if (c.col_type === "date") return "Dates";
  if (n.includes("note")) return "Notes";
  return "Other";
}
const GROUP_ORDER = ["Core competencies", "Certifications & training", "AISC element training", "Dates", "Notes", "Other"];

export default function SkillsMatrix() {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [colOpen, setColOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [emp, setEmp] = useState({ name: "", title: "", employment_status: "", shift: "", section: "", team_id: "" });
  const [col, setCol] = useState({ name: "", col_type: "rating" });
  const [teamName, setTeamName] = useState("");
  const [editRow, setEditRow] = useState(null);
  const [teamFilter, setTeamFilter] = useState("all");
  const [collapsedGroups, setCollapsedGroups] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem("sm_groups") || "[]")); } catch { return new Set(); } });
  const [collapsedTeams, setCollapsedTeams] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem("sm_teams") || "[]")); } catch { return new Set(); } });

  const load = async () => {
    setLoading(true);
    try { const r = await ApiCall.get("/skills-matrix"); const d = r.data?.data || {}; setColumns(d.columns || []); setRows(d.rows || []); setTeams(d.teams || []); }
    catch { showErrorToast("Failed to load the skills matrix"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const ratingCols = useMemo(() => columns.filter((c) => c.col_type === "rating"), [columns]);
  const groups = useMemo(() => {
    const by = {}; columns.forEach((c) => { const g = groupOf(c); (by[g] = by[g] || []).push(c); });
    return GROUP_ORDER.filter((g) => by[g]).map((g) => ({ name: g, cols: by[g] }));
  }, [columns]);

  const toggleGroup = (g) => setCollapsedGroups((s) => { const n = new Set(s); n.has(g) ? n.delete(g) : n.add(g); localStorage.setItem("sm_groups", JSON.stringify([...n])); return n; });
  const toggleTeam = (k) => setCollapsedTeams((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); localStorage.setItem("sm_teams", JSON.stringify([...n])); return n; });

  const setLocal = (rid, cid, v) => setRows((rs) => rs.map((r) => (r.id === rid ? { ...r, cells: { ...r.cells, [cid]: v } } : r)));
  const save = async (rid, cid, v) => { try { await ApiCall.put("/skills-matrix/cell", { row_id: rid, column_id: cid, value: v }); } catch { showErrorToast("Save failed"); load(); } };
  const avg = (r) => { const vs = ratingCols.map((c) => Number(r.cells[c.id])).filter((n) => Number.isFinite(n)); return vs.length ? (vs.reduce((a, b) => a + b, 0) / vs.length).toFixed(1) : "—"; };
  const setRowTeam = async (rid, team_id) => { setRows((rs) => rs.map((r) => (r.id === rid ? { ...r, team_id: team_id || null } : r))); try { await ApiCall.put(`/skills-matrix/row/${rid}`, { team_id: team_id || null }); } catch { showErrorToast("Failed"); load(); } };

  const addEmployee = async () => { if (!emp.name.trim()) return; try { await ApiCall.post("/skills-matrix/row", emp); showSuccessToast("Employee added"); setAddOpen(false); setEmp({ name: "", title: "", employment_status: "", shift: "", section: "", team_id: "" }); load(); } catch { showErrorToast("Failed to add"); } };
  const addColumn = async () => { if (!col.name.trim()) return; try { await ApiCall.post("/skills-matrix/column", col); showSuccessToast("Skill added"); setColOpen(false); setCol({ name: "", col_type: "rating" }); load(); } catch { showErrorToast("Failed to add"); } };
  const addTeam = async () => { if (!teamName.trim()) return; try { await ApiCall.post("/skills-matrix/team", { name: teamName.trim() }); showSuccessToast("Team created"); setTeamOpen(false); setTeamName(""); load(); } catch { showErrorToast("Failed"); } };
  const saveEdit = async () => {
    if (!editRow || !editRow.name.trim()) return;
    try {
      await ApiCall.put(`/skills-matrix/row/${editRow.id}`, {
        name: editRow.name, title: editRow.title, employment_status: editRow.employment_status,
        shift: editRow.shift, section: editRow.section, team_id: editRow.team_id || "",
      });
      showSuccessToast("Employee updated"); setEditRow(null); load();
    } catch { showErrorToast("Failed to update"); }
  };
  const removeRow = async (id, name) => { if (!window.confirm(`Remove ${name} from the matrix?`)) return; try { await ApiCall.delete(`/skills-matrix/row/${id}`); load(); } catch { showErrorToast("Failed"); } };

  // group rows by team
  const teamName_ = (tid) => { const t = teams.find((x) => x.id === tid); return t ? t.name : null; };
  const visibleRows = teamFilter === "all" ? rows : teamFilter === "none" ? rows.filter((r) => !r.team_id) : rows.filter((r) => String(r.team_id) === String(teamFilter));
  const grouped = useMemo(() => {
    const map = new Map();
    visibleRows.forEach((r) => { const k = r.team_id != null ? String(r.team_id) : "__none__"; if (!map.has(k)) map.set(k, []); map.get(k).push(r); });
    const order = [...teams.map((t) => String(t.id)), "__none__"];
    return order.filter((k) => map.has(k)).map((k) => ({ key: k, label: k === "__none__" ? "Unassigned" : teamName_(Number(k)), rows: map.get(k) }));
  }, [visibleRows, teams]);

  const totalCols = 3 + groups.reduce((a, g) => a + (collapsedGroups.has(g.name) ? 1 : g.cols.length), 0) + 1;

  return (
    <Container maxWidth={false} sx={{ py: 1 }}>
      <Helmet><title>Employee Skills Matrix | SFM</title></Helmet>
      <PageHeader
        breadcrumbs={[{ label: "Roles", to: "/roles" }, { label: "Employee Skills Matrix" }]}
        title="Employee Skills Matrix"
        action={<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <TextField select size="small" label="Team" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="all">All teams</MenuItem>
            {teams.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
            <MenuItem value="none">Unassigned</MenuItem>
          </TextField>
          <Button variant="outlined" startIcon={<GroupsRounded />} onClick={() => setTeamOpen(true)}>Team</Button>
          <Button variant="outlined" startIcon={<AddRounded />} onClick={() => setColOpen(true)}>Skill</Button>
          <Button variant="contained" startIcon={<AddRounded />} onClick={() => setAddOpen(true)}>Employee</Button>
        </Box>}
        guide={{
          why: "Show at a glance who is competent at what, and organize people into work groups - so jobs go to qualified teams and training gaps are visible.",
          who: "Supervisors and the QM, assigning qualified people and spotting training gaps.",
          what: "A competence matrix rating each employee 0-9 across shop skills, with certs, training records, and team assignments.",
          how: "Click a cell to set a rating; click a column-group header to collapse it; assign people to teams and filter by team.",
          when: "Keep it current as people are trained, reviewed, certified, and moved between crews.",
          standard: "AISC 207-25 §1.14 · ISO 9001:2015 §7.2",
        }}
      />
      {loading ? <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box> : (
        <Box sx={{ overflow: "auto", border: "1px solid #E3E6EA", borderRadius: 1.5, maxHeight: "72vh" }}>
          <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", whiteSpace: "nowrap" }}>
            <thead>
              <tr>
                <th style={{ ...stickyHead, background: "#1D2330" }} rowSpan={2}>Employee</th>
                <th style={head} rowSpan={2}>Title</th>
                <th style={head} rowSpan={2}>Overall</th>
                {groups.map((g) => {
                  const col = collapsedGroups.has(g.name);
                  return <th key={g.name} colSpan={col ? 1 : g.cols.length} style={grpHead} onClick={() => toggleGroup(g.name)} title="Click to collapse / expand">
                    {col ? "▸ " : "▾ "}{g.name}{col ? ` (${g.cols.length})` : ""}
                  </th>;
                })}
                <th style={head} rowSpan={2}></th>
              </tr>
              <tr>
                {groups.map((g) => collapsedGroups.has(g.name)
                  ? <th key={g.name} style={{ ...head, color: "#8893a0" }}>…</th>
                  : g.cols.map((c) => <th key={c.id} style={{ ...head, ...(c.col_type !== "rating" ? { minWidth: 120 } : {}) }} title={c.name}>{c.name}</th>))}
              </tr>
            </thead>
            <tbody>
              {grouped.map((tg) => (
                <Fragment key={tg.key}>
                  <tr key={"h" + tg.key}>
                    <td colSpan={totalCols} style={{ background: "#F4F5F7", padding: "5px 10px", fontWeight: 700, fontSize: "0.74rem", color: "#374151", cursor: "pointer", position: "sticky", left: 0, borderBottom: "1px solid #E3E6EA" }} onClick={() => toggleTeam(tg.key)}>
                      {collapsedTeams.has(tg.key) ? "▸ " : "▾ "}{tg.label} <span style={{ color: "#9aa3ad", fontWeight: 500 }}>({tg.rows.length})</span>
                    </td>
                  </tr>
                  {!collapsedTeams.has(tg.key) && tg.rows.map((r) => (
                    <tr key={r.id}>
                      <td style={stickyCell}>
                        <strong>{r.name}</strong>
                        <div style={{ fontSize: "0.62rem", color: "#999", display: "flex", alignItems: "center", gap: 4 }}>
                          {r.section || ""}{r.shift ? ` · Sh ${r.shift}` : ""}
                          <select value={r.team_id || ""} onChange={(e) => setRowTeam(r.id, e.target.value)} style={{ fontSize: "0.6rem", border: "none", background: "transparent", color: "#F88F1D", cursor: "pointer" }} title="Assign team">
                            <option value="">+ team</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                        </div>
                      </td>
                      <td style={cell}>{r.title || "—"}</td>
                      <td style={cell}>{(() => { const a = avg(r); const n = Number(a); return <span style={{ display: "inline-block", minWidth: 30, padding: "3px 7px", borderRadius: 12, fontWeight: 800, fontSize: "0.74rem", background: a === "—" ? "#F0F1F3" : ratingBg(n), color: a === "—" ? "#9aa3ad" : ratingFg(n) }}>{a}</span>; })()}</td>
                      {groups.map((g) => collapsedGroups.has(g.name)
                        ? <td key={g.name} style={{ ...cell, color: "#cfd4da" }}>·</td>
                        : g.cols.map((c) => {
                          const v = r.cells[c.id] ?? "";
                          if (c.col_type === "rating") return (
                            <td key={c.id} style={{ ...cell, background: ratingBg(v), padding: 0 }}>
                              <select value={v} onChange={(e) => { setLocal(r.id, c.id, e.target.value); save(r.id, c.id, e.target.value); }}
                                style={{ width: 44, border: "none", background: "transparent", textAlign: "center", color: ratingFg(v), fontWeight: 700, padding: "6px 0", cursor: "pointer", appearance: "none" }}>
                                <option value=""></option>{[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => <option key={n} value={n}>{n}</option>)}
                              </select>
                            </td>);
                          return (
                            <td key={c.id} style={{ ...cell, padding: 0 }}>
                              <input type={c.col_type === "date" ? "date" : "text"} defaultValue={v}
                                onBlur={(e) => { if (e.target.value !== v) { setLocal(r.id, c.id, e.target.value); save(r.id, c.id, e.target.value); } }}
                                style={{ width: 120, border: "none", background: "transparent", padding: "6px", fontSize: "0.78rem" }} />
                            </td>);
                        }))}
                      <td style={cell}><Box sx={{ display: "flex", gap: 0.25 }}><IconButton size="small" onClick={() => setEditRow({ id: r.id, name: r.name || "", title: r.title || "", employment_status: r.employment_status || "", shift: r.shift || "", section: r.section || "", team_id: r.team_id || "" })} sx={{ color: "#1D2330" }}><EditOutlined fontSize="small" /></IconButton><IconButton size="small" onClick={() => removeRow(r.id, r.name)} sx={{ color: "error.main" }}><DeleteOutline fontSize="small" /></IconButton></Box></td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </Box>
      )}
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
        {rows.length} employees · {teams.length} teams · {ratingCols.length} rated skills · per AISC 207-25 §1.14 / ISO 9001:2015 §7.2.
      </Typography>

      <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#1D2330", color: "#fff" }}>Edit Employee</DialogTitle>
        <DialogContent dividers>
          {editRow && <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField label="Name" size="small" value={editRow.name} onChange={(e) => setEditRow({ ...editRow, name: e.target.value })} autoFocus />
            <TextField label="Title" size="small" value={editRow.title} onChange={(e) => setEditRow({ ...editRow, title: e.target.value })} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Employment status" size="small" fullWidth value={editRow.employment_status} onChange={(e) => setEditRow({ ...editRow, employment_status: e.target.value })} />
              <TextField label="Shift" size="small" sx={{ width: 110 }} value={editRow.shift} onChange={(e) => setEditRow({ ...editRow, shift: e.target.value })} />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Section (Shop / Field)" size="small" fullWidth value={editRow.section} onChange={(e) => setEditRow({ ...editRow, section: e.target.value })} />
              <TextField select label="Team" size="small" sx={{ width: 160 }} value={editRow.team_id} onChange={(e) => setEditRow({ ...editRow, team_id: e.target.value })}>
                <MenuItem value="">No team</MenuItem>{teams.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
            </Box>
          </Box>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setEditRow(null)}>Cancel</Button><Button variant="contained" onClick={saveEdit}>Save</Button></DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#1D2330", color: "#fff" }}>Add Employee</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField label="Name" size="small" value={emp.name} onChange={(e) => setEmp({ ...emp, name: e.target.value })} autoFocus />
            <TextField label="Title" size="small" value={emp.title} onChange={(e) => setEmp({ ...emp, title: e.target.value })} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Employment status" size="small" fullWidth value={emp.employment_status} onChange={(e) => setEmp({ ...emp, employment_status: e.target.value })} />
              <TextField label="Shift" size="small" sx={{ width: 110 }} value={emp.shift} onChange={(e) => setEmp({ ...emp, shift: e.target.value })} />
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField label="Section (Shop / Field)" size="small" fullWidth value={emp.section} onChange={(e) => setEmp({ ...emp, section: e.target.value })} />
              <TextField select label="Team" size="small" sx={{ width: 160 }} value={emp.team_id} onChange={(e) => setEmp({ ...emp, team_id: e.target.value })}>
                <MenuItem value="">No team</MenuItem>{teams.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setAddOpen(false)}>Cancel</Button><Button variant="contained" onClick={addEmployee}>Add</Button></DialogActions>
      </Dialog>

      <Dialog open={colOpen} onClose={() => setColOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: "#1D2330", color: "#fff" }}>Add Skill / Column</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField label="Skill / column name" size="small" value={col.name} onChange={(e) => setCol({ ...col, name: e.target.value })} autoFocus />
            <TextField select label="Type" size="small" value={col.col_type} onChange={(e) => setCol({ ...col, col_type: e.target.value })}>
              <MenuItem value="rating">Rating (0–9)</MenuItem><MenuItem value="text">Text</MenuItem><MenuItem value="date">Date</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setColOpen(false)}>Cancel</Button><Button variant="contained" onClick={addColumn}>Add</Button></DialogActions>
      </Dialog>

      <Dialog open={teamOpen} onClose={() => setTeamOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: "#1D2330", color: "#fff" }}>Create Work Group / Team</DialogTitle>
        <DialogContent dividers>
          <TextField label="Team name" size="small" fullWidth sx={{ mt: 1 }} value={teamName} onChange={(e) => setTeamName(e.target.value)} autoFocus placeholder="e.g., Crew A, Erection Team, Day Shift" />
          {teams.length > 0 && <Box sx={{ mt: 2 }}><Typography variant="caption" sx={{ color: "text.secondary" }}>Existing teams:</Typography>
            {teams.map((t) => <Box key={t.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}><span>{t.name}</span><IconButton size="small" sx={{ color: "error.main" }} onClick={async () => { await ApiCall.delete(`/skills-matrix/team/${t.id}`); load(); }}><DeleteOutline fontSize="small" /></IconButton></Box>)}
          </Box>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setTeamOpen(false)}>Close</Button><Button variant="contained" onClick={addTeam}>Create Team</Button></DialogActions>
      </Dialog>
    </Container>
  );
}
